import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

// Self-hosted brand fonts (bundled by Vite, served same-origin) so the strict
// CSP needs no third-party font/style origins. Families match the brand tokens
// ("Inter", "IBM Plex Mono").
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/ibm-plex-mono/400.css";
import "@fontsource/ibm-plex-mono/500.css";

import "@/client/index.css";
import { applyTheme, getTheme } from "@/client/lib/theme";
import { AuthGate } from "@/client/components/auth-gate";
import { RootLayout } from "@/client/components/root-layout";
import { RouteError } from "@/client/components/route-error";
import { AcceptInvitePage } from "@/client/pages/auth/accept-invite";
import { AuthLayout } from "@/client/pages/auth/auth-layout";
import { ForgotPasswordPage } from "@/client/pages/auth/forgot-password";
import { LoginPage } from "@/client/pages/auth/login";
import { ResetPasswordPage } from "@/client/pages/auth/reset-password";
import { VerifyEmailPage } from "@/client/pages/auth/verify-email";
import { ApiTokensPage } from "@/client/pages/customer/api-tokens";
import { CalendarPage } from "@/client/pages/customer/calendar";
import { CustomerShell } from "@/client/pages/customer/customer-shell";
import { CustomerDashboard } from "@/client/pages/customer/dashboard";
import { DocsPage } from "@/client/pages/customer/docs";
import {
  NodeConsolePowerTab,
  NodeDetailPage,
  NodeOverviewTab,
  NodePolicyTab,
  NodeRedfishTab,
} from "@/client/pages/customer/node-detail";
import { NodesPage } from "@/client/pages/customer/nodes";
import { PxePage } from "@/client/pages/customer/pxe";
import { SettingsPage } from "@/client/pages/customer/settings";
import { SshKeysPage } from "@/client/pages/customer/ssh-keys";
import { StoragePage } from "@/client/pages/customer/storage";
import { AllowedIpsPage } from "@/client/pages/customer/allowed-ips";
import { TeamPage } from "@/client/pages/customer/team";
import { TicketDetailPage, TicketsPage } from "@/client/pages/customer/tickets";
import { NotFound } from "@/client/pages/not-found";
import {
  CapacityRequestPage,
  CapacityRequestVerifyPage,
} from "@/client/pages/request/capacity-request";
import { StaffDashboard } from "@/client/pages/staff/dashboard";
import { StaffAuditLogPage } from "@/client/pages/staff/audit-log";
import { StaffDocsPage } from "@/client/pages/staff/docs";
import { StaffDocsPlacementsPage } from "@/client/pages/staff/docs-placements";
import { StaffInquiriesPage } from "@/client/pages/staff/inquiries";
import { StaffInquiryDetailPage } from "@/client/pages/staff/inquiry-detail";
import { StaffMaintenancePage } from "@/client/pages/staff/maintenance";
import { StaffShell } from "@/client/pages/staff/staff-shell";
import { StaffTenantDetailPage } from "@/client/pages/staff/tenant-detail";
import { StaffTenantsPage } from "@/client/pages/staff/tenants";
import { StaffUsersPage } from "@/client/pages/staff/users";

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      {
        path: "/request",
        element: <CapacityRequestPage />,
      },
      {
        path: "/request/verify",
        element: <CapacityRequestVerifyPage />,
      },
      {
        element: <AuthLayout />,
        children: [
          { path: "/login", element: <LoginPage /> },
          { path: "/accept-invite", element: <AcceptInvitePage /> },
          { path: "/forgot-password", element: <ForgotPasswordPage /> },
          { path: "/reset-password", element: <ResetPasswordPage /> },
          { path: "/verify-email", element: <VerifyEmailPage /> },
        ],
      },
      {
        element: <AuthGate audience="customer" />,
        children: [
          {
            element: <CustomerShell />,
            children: [
              { index: true, element: <CustomerDashboard /> },
              { path: "docs", element: <DocsPage /> },
              { path: "docs/:slug", element: <DocsPage /> },
              { path: "calendar", element: <CalendarPage /> },
              { path: "team", element: <TeamPage /> },
              { path: "tickets", element: <TicketsPage /> },
              { path: "tickets/:id", element: <TicketDetailPage /> },
              { path: "nodes", element: <NodesPage /> },
              {
                path: "nodes/:id",
                element: <NodeDetailPage />,
                children: [
                  { index: true, element: <NodeOverviewTab /> },
                  { path: "console", element: <NodeConsolePowerTab /> },
                  { path: "redfish", element: <NodeRedfishTab /> },
                  { path: "policy", element: <NodePolicyTab /> },
                ],
              },
              { path: "pxe", element: <PxePage /> },
              { path: "ssh-keys", element: <SshKeysPage /> },
              { path: "storage", element: <StoragePage /> },
              { path: "allowed-ips", element: <AllowedIpsPage /> },
              { path: "api-tokens", element: <ApiTokensPage /> },
              { path: "settings", element: <SettingsPage /> },
            ],
          },
        ],
      },
      {
        path: "admin",
        element: <AuthGate audience="staff" />,
        children: [
          {
            element: <StaffShell />,
            children: [
              { index: true, element: <StaffDashboard /> },
              { path: "inquiries", element: <StaffInquiriesPage /> },
              { path: "inquiries/:id", element: <StaffInquiryDetailPage /> },
              { path: "tenants", element: <StaffTenantsPage /> },
              { path: "tenants/:id", element: <StaffTenantDetailPage /> },
              { path: "users", element: <StaffUsersPage /> },
              {
                path: "audit-log",
                element: <StaffAuditLogPage />,
                // The audit table has many columns and wide values; opt out of
                // the default reading-width cap so it can use the full viewport.
                handle: { fullWidth: true },
              },
              { path: "docs", element: <StaffDocsPage /> },
              { path: "placements", element: <StaffDocsPlacementsPage /> },
              { path: "maintenance", element: <StaffMaintenancePage /> },
            ],
          },
        ],
      },
      { path: "*", element: <NotFound /> },
    ],
  },
]);

// Reconcile the DOM with the computed theme in the bundled path, so the app's
// state and the <html> classes agree even if the pre-paint /theme-init.js was
// blocked (e.g. CSP) or threw. theme-init.js still runs first to avoid a flash.
applyTheme(getTheme());

const rootEl = document.getElementById("root");
if (!rootEl) {
  throw new Error("Root element #root not found");
}

createRoot(rootEl).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
